// src/redux/reducers/InvoiceManager.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DocType, Document, Invoice2, QboAccount, QboCustomer, QboVendor, TransactionLine } from "../../docuclipper/DocuclipperTypes";
import { cloneDeep, uniq } from "lodash";
import {
  bulkApplyInvoiceLineItemField,
  createQboBill2,
  createQboExpense2,
  createQboInvoice2,
  createXeroBill2,
  createXeroInvoice2,
  getJobTransactions,
  getJobTransactionsWithTagLevels,
  runInvoiceRules,
  runInvoiceRulesTagLevel,
  updateJobTransaction,
  updateJobTransactionTagLevel,
} from "../../docuclipper/api";
import { ReduxState } from "..";
import { isNullOrUndefined } from "src/utils/utils";
import { createAlert } from "./Alerts";
import { SUPPORTED_SCOPES } from "./TagLevelIntegration";
import {
  trackInvoiceExportAttempted,
  trackInvoiceExportSucceeded,
  trackInvoiceExportFailed,
  InvoiceExportTarget,
} from "src/utils/invoiceExportAnalytics";

const initialState: {
  documentId: string | null;
  transactionLineRowNumber: number | null;
  transactionLines: TransactionLine[];
  loading: boolean;
  importToQboLoading: boolean;
  runInvoiceRulesLoading: boolean;
  error: string | null;
  documents: Document[];
  downloadOptions: {
    selectedDocumentIds: string[];
    selectedFieldNames: string[];
  };
  showModalInvoiceRules: boolean;
  validationErrors: Record<string, {
    valid: boolean;
    errors: {
      property: string;
      message: string;
      name: string;
    }[];
  }>;
  duplicateError: {
    qboUrl: string;
    docType: DocType;
  } | null;
  invoiceFieldStandard: string[];
  invoiceFieldExtra: string[];
  lineItemFieldStandard: string[];
  lineItemFieldExtra: string[];
  // Auto-save indicator state. `saving` flips true while a field-edit
  // request is in flight; `lastSavedAt` records the success timestamp so
  // we can render "Saved 12s ago" when idle.
  saving: boolean;
  lastSavedAt: number | null;
  saveError: string | null;
} = {
  documentId: null,
  transactionLineRowNumber: 0,
  transactionLines: [],
  loading: false,
  importToQboLoading: false,
  runInvoiceRulesLoading: false,
  error: null,
  documents: [],
  downloadOptions: {
    selectedDocumentIds: [],
    selectedFieldNames: [],
  },
  showModalInvoiceRules: false,
  validationErrors: {},
  duplicateError: null,
  invoiceFieldStandard: [],
  invoiceFieldExtra: [],
  lineItemFieldStandard: [],
  lineItemFieldExtra: [],
  saving: false,
  lastSavedAt: null,
  saveError: null,
};

const invoiceManagerSlice = createSlice({
  name: "InvoiceManager",
  initialState,
  reducers: {
    setDocumentId(state, action: PayloadAction<string>) {
      state.documentId = action.payload;
      state.transactionLineRowNumber = 0;
    },
    setTransactionLineRowNumber(state, action: PayloadAction<number>) {
      state.transactionLineRowNumber = action.payload;
    },
    updateTransactionLines(state, action: PayloadAction<TransactionLine[]>) {
      state.transactionLines = action.payload;
    },
    loadTls(state) {
      state.loading = true;
      state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    saveStart(state) {
      state.saving = true;
      state.saveError = null;
    },
    saveSuccess(state) {
      state.saving = false;
      state.lastSavedAt = Date.now();
      state.saveError = null;
    },
    saveError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.saveError = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setDocuments(state, action: PayloadAction<Document[]>) {
      state.documents = action.payload;
    },
    setDownloadOptionsSelectedDocumentIds(
      state,
      action: PayloadAction<string[]>
    ) {
      state.downloadOptions.selectedDocumentIds = action.payload;
    },
    setDownloadOptionsSelectedFieldNames(
      state,
      action: PayloadAction<string[]>
    ) {
      state.downloadOptions.selectedFieldNames = action.payload;
    },
    setImportToQboLoading(state, action: PayloadAction<boolean>) {
      state.importToQboLoading = action.payload;
    },
    setShowModalInvoiceRules(state, action: PayloadAction<boolean>) {
      state.showModalInvoiceRules = action.payload;
    },
    setRunInvoiceRulesLoading(state, action: PayloadAction<boolean>) {
      state.runInvoiceRulesLoading = action.payload;
    },
    setValidationErrors(state, action: PayloadAction<Record<string, {
      valid: boolean;
      errors: {
        property: string;
        message: string;
        name: string;
      }[];
    }>>) {
      state.validationErrors = action.payload;
    },
    setDuplicateError(state, action: PayloadAction<{
      qboUrl: string;
      docType: DocType;
    } | null>) {
      state.duplicateError = action.payload;
    },
    setInvoiceFieldStandard(state, action: PayloadAction<string[]>) {
      state.invoiceFieldStandard = action.payload;
    },
    setInvoiceFieldExtra(state, action: PayloadAction<string[]>) {
      state.invoiceFieldExtra = action.payload;
    },
    setLineItemFieldStandard(state, action: PayloadAction<string[]>) {
      state.lineItemFieldStandard = action.payload;
    },
    setLineItemFieldExtra(state, action: PayloadAction<string[]>) {
      state.lineItemFieldExtra = action.payload;
    },
  },
});

export const {
  setDocumentId,
  setTransactionLineRowNumber,
  updateTransactionLines,
  setLoading,
  setError,
  setDocuments,
  setDownloadOptionsSelectedDocumentIds,
  setDownloadOptionsSelectedFieldNames,
  setImportToQboLoading,
  setShowModalInvoiceRules,
  setRunInvoiceRulesLoading,
  setValidationErrors,
  setDuplicateError,
  setInvoiceFieldStandard,
  setInvoiceFieldExtra,
  setLineItemFieldStandard,
  setLineItemFieldExtra,
  saveStart,
  saveSuccess,
  saveError,
} = invoiceManagerSlice.actions;

export const fetchInvoiceTls = (invoiceId?) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  const job = state.JobData.job;

  // if (!job) return;

  try {
    dispatch(setLoading(true));
    let res;
    if (job && job.id) {
      res = await getJobTransactions(job.id);
    }
    else {
      res = await getJobTransactionsWithTagLevels(invoiceId)
    }
    const { transactions: tls, documents, validations } = res
    const transactions: TransactionLine[] = tls.sort((a, b) => a.rowNumber - b.rowNumber);
    const allFields = uniq<string>(tls.map((x) => x.fieldName));
    dispatch(setDocuments(documents));
    if (tls.length > 0) {
      dispatch(setDocumentId(tls[0].documentId));
    }
    dispatch(updateTransactionLines(transactions));
    dispatch(
      setDownloadOptionsSelectedDocumentIds(
        uniq(tls.map((x) => x.documentId))
      )
    );
    dispatch(setDownloadOptionsSelectedFieldNames(allFields));


    dispatch(setValidationErrors(validations));
  } catch (err: any) {
    dispatch(setError(err.message || "Error getting job transactions"));
  } finally {
    dispatch(setLoading(false));
  }
};

// Module-level set of transactionLine ids that currently have a save in
// flight. Used to dedupe concurrent dispatches (e.g. a normalizer effect
// re-firing before the previous save resolves) which would otherwise loop.
const inFlightTransactionLineIds = new Set<string>();

export const handleOnUpdateJobTransaction = (invoice2: Invoice2, transactionLine: TransactionLine) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  // const job = state.JobData.job;

  if (!transactionLine?.id) return;

  const inFlightKey = String(transactionLine.id);
  if (inFlightTransactionLineIds.has(inFlightKey)) {
    // A save for this transaction line is already in flight — drop the
    // duplicate dispatch to avoid the infinite save loop.
    return;
  }
  inFlightTransactionLineIds.add(inFlightKey);

  dispatch(saveStart());
  try {
    const { transaction, validation } = await updateJobTransactionTagLevel(
      Number(transactionLine.id),
      { newValue: JSON.stringify(invoice2) }
    );

    const selectedTlIndex = state.InvoiceManager.transactionLines.findIndex(
      (tl) => tl.documentId === state.InvoiceManager.documentId && tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
    );

    dispatch(updateTransactionLines(
      state.InvoiceManager.transactionLines.map((tl, index) => {
        if (index === selectedTlIndex) {
          return {
            ...tl,
            row: JSON.stringify(invoice2) as any,
            // Update metadata from the API response to refresh Export & Sync section
            metadata: transaction?.metadata ?? tl.metadata,
          }
        }
        return tl;
      })
    ));
    dispatch(setValidationErrors({ ...state.InvoiceManager.validationErrors, [transaction.id]: validation }))
    dispatch(saveSuccess());
  } catch (err) {
    console.error("Error updating invoice:", err);
    // Persist the optimistic row even on error so subsequent normalizer
    // effects don't see stale pre-save values (e.g. "payables") and re-fire
    // forever. Mirrors the success branch's row update.
    const selectedTlIndex = state.InvoiceManager.transactionLines.findIndex(
      (tl) => tl.documentId === state.InvoiceManager.documentId && tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
    );
    if (selectedTlIndex !== -1) {
      dispatch(updateTransactionLines(
        state.InvoiceManager.transactionLines.map((tl, index) => {
          if (index === selectedTlIndex) {
            return {
              ...tl,
              row: JSON.stringify(invoice2) as any,
            }
          }
          return tl;
        })
      ));
    }
    dispatch(
      saveError((err as any)?.message || "Save failed"),
    );
    dispatch(
      createAlert({
        message: (err as any)?.message || "Failed to update invoice field. Please try again.",
        type: "error",
        timeout: 5000,
      })
    );
  } finally {
    inFlightTransactionLineIds.delete(inFlightKey);
  }
};

export const updateInvoiceField = ({ invoice, field, value }: { invoice: Invoice2, field: string, value: any }) => (dispatch, getState) => {
  const state: ReduxState = getState();
  const transactionLine = cloneDeep(state.InvoiceManager.transactionLines.find(
    (tl) => tl.documentId === state.InvoiceManager.documentId && tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
  ));
  if (transactionLine) {
    invoice[field] = value;
    transactionLine.row = JSON.stringify(invoice) as any;
    dispatch(handleOnUpdateJobTransaction(invoice, transactionLine));
  }
};

export const updateInvoiceLineItem = (
  { invoice, lineIndex, field, value }: { invoice: Invoice2, lineIndex: number, field: string, value: any }
) => (dispatch, getState) => {
  const state: ReduxState = getState();
  const transactionLine = cloneDeep(state.InvoiceManager.transactionLines.find(
    (tl) => tl.documentId === state.InvoiceManager.documentId && tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
  ));
  if (transactionLine) {
    invoice.lines[lineIndex][field] = ["quantity", "unitPrice", "price"].includes(field) && value
      ? parseFloat(value)
      : value;

    // Handle name fields automatically (QuickBooks)
    if (field === "categoryEdited") {
      invoice.lines[lineIndex].categoryEditedName = value ? state.QboAccountCategory.qboAccountCategory.find(acc => acc.value === value)?.name || "" : "";
      invoice.lines[lineIndex].categoryAutomated = "";
    }
    if (field === "serviceEdited") {
      invoice.lines[lineIndex].serviceEditedName = value ? state.QboItem.qboItems.find(item => item.value === value)?.name || "" : "";
      invoice.lines[lineIndex].serviceAutomated = "";
    }
    // Handle name fields automatically (Xero)
    if (field === "categoryXeroEdited") {
      invoice.lines[lineIndex].categoryXeroEditedName = value ? state.XeroAccount.xeroAccounts.find(acc => acc.value === value)?.name || "" : "";
      invoice.lines[lineIndex].categoryXeroAutomated = "";
    }
    if (field === "serviceXeroEdited") {
      invoice.lines[lineIndex].serviceXeroEditedName = value ? state.XeroItem.xeroItems.find(item => item.value === value)?.name || "" : "";
      invoice.lines[lineIndex].serviceXeroAutomated = "";
    }
    if (field === "taxXeroEdited") {
      invoice.lines[lineIndex].taxXeroEditedName = value ? state.XeroTaxRate.xeroTaxRates.find(tax => tax.value === value)?.name || "" : "";
      invoice.lines[lineIndex].taxXeroAutomated = "";
    }
    if (field === "categoryExcelEdited") {
      invoice.lines[lineIndex].categoryExcelAutomated = "";
    }
    if (field === "serviceExcelEdited") {
      invoice.lines[lineIndex].serviceExcelAutomated = "";
    }
    if (field === "taxEdited") {
      invoice.lines[lineIndex].taxEditedName = value ? state.QboTaxCode.qboTaxCodes.find(tax => tax.value === value)?.name || "" : "";
    }

    transactionLine.row = JSON.stringify(invoice) as any;
    dispatch(handleOnUpdateJobTransaction(invoice, transactionLine));
  }
};

// (field, target) -> the LineItem blob keys the value/name land on. Mirrors the
// backend FIELD_COLUMN_MAP in invoice-line-item-handler.ts so the optimistic
// local update writes to exactly the same columns the server persists. `name`
// is only carried by qbo/xero (the *EditedName columns).
type BulkField = "category" | "service" | "tax";
type BulkTarget = "qbo" | "excel" | "xero" | "dynamics" | "sage";
const BULK_FIELD_COLUMN_MAP: Record<
  BulkField,
  Record<BulkTarget, { editedKey: string; editedNameKey?: string }>
> = {
  category: {
    qbo: { editedKey: "categoryEdited", editedNameKey: "categoryEditedName" },
    excel: { editedKey: "categoryExcelEdited" },
    xero: { editedKey: "categoryXeroEdited", editedNameKey: "categoryXeroEditedName" },
    dynamics: { editedKey: "categoryDynamicsEdited" },
    sage: { editedKey: "categorySageEdited" },
  },
  service: {
    qbo: { editedKey: "serviceEdited", editedNameKey: "serviceEditedName" },
    excel: { editedKey: "serviceExcelEdited" },
    xero: { editedKey: "serviceXeroEdited", editedNameKey: "serviceXeroEditedName" },
    dynamics: { editedKey: "serviceDynamicsEdited" },
    sage: { editedKey: "serviceSageEdited" },
  },
  tax: {
    qbo: { editedKey: "taxEdited", editedNameKey: "taxEditedName" },
    excel: { editedKey: "taxExcelEdited" },
    xero: { editedKey: "taxXeroEdited" },
    dynamics: { editedKey: "taxDynamicsEdited" },
    sage: { editedKey: "taxSageEdited" },
  },
};

// Bulk-apply one classification field to ALL line items of the currently
// selected invoice (the "Apply to all line items" fill-down affordance).
// Calls the single-invoice bulkApply endpoint (no lineIndexes => all lines),
// then optimistically writes the same value/name into every line of the local
// blob so the grid reflects the change without a full reload. Returns the
// number of lines updated on success; throws on failure so the caller can show
// an error state.
export const applyLineItemFieldToAllLines = (
  { field, target, value, name }: {
    field: BulkField;
    target: BulkTarget;
    value: string;
    name?: string;
  }
) => async (dispatch, getState): Promise<number> => {
  const state: ReduxState = getState();
  const transactionLine = cloneDeep(
    state.InvoiceManager.transactionLines.find(
      (tl) =>
        tl.documentId === state.InvoiceManager.documentId &&
        tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
    )
  );
  if (!transactionLine?.id) {
    throw new Error("No invoice selected");
  }

  const cols = BULK_FIELD_COLUMN_MAP[field]?.[target];
  if (!cols) {
    throw new Error(`Unsupported field/target: ${field}/${target}`);
  }

  dispatch(saveStart());
  try {
    const body: {
      field: BulkField;
      target: BulkTarget;
      value: string;
      name?: string;
    } = { field, target, value };
    // name only matters for targets that carry an *EditedName column.
    if (cols.editedNameKey && typeof name !== "undefined") {
      body.name = name;
    }

    const res = await bulkApplyInvoiceLineItemField(
      Number(transactionLine.id),
      body
    );

    // Optimistically mirror the server write into the local blob so all lines
    // update in place. The server is the source of truth for `row`; we write
    // the same columns it did.
    const invoice = transactionLine.row
      ? (JSON.parse(transactionLine.row as any) as Invoice2)
      : null;
    if (invoice && Array.isArray(invoice.lines)) {
      invoice.lines = invoice.lines.map((line: any) => {
        const updated = { ...line, [cols.editedKey]: value };
        if (cols.editedNameKey) {
          updated[cols.editedNameKey] = typeof name !== "undefined" ? name : "";
        }
        return updated;
      });
      transactionLine.row = JSON.stringify(invoice) as any;

      const selectedTlIndex = state.InvoiceManager.transactionLines.findIndex(
        (tl) =>
          tl.documentId === state.InvoiceManager.documentId &&
          tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
      );
      dispatch(
        updateTransactionLines(
          state.InvoiceManager.transactionLines.map((tl, index) =>
            index === selectedTlIndex ? { ...tl, row: transactionLine.row } : tl
          )
        )
      );
    }

    dispatch(saveSuccess());
    return res.linesUpdated;
  } catch (err) {
    dispatch(saveError((err as any)?.message || "Bulk apply failed"));
    dispatch(
      createAlert({
        message:
          (err as any)?.message ||
          "Failed to apply the value to all line items. Please try again.",
        type: "error",
        timeout: 5000,
      })
    );
    throw err;
  }
};

export const handleImportToQbo = (values: Invoice2, forceImport: boolean = false) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  const job = state.JobData.job;

  const selectedTl = state.InvoiceManager.transactionLines.find(
    (tl) => tl.documentId === state.InvoiceManager.documentId && tl.rowNumber === state.InvoiceManager.transactionLineRowNumber
  );

  const invoice = selectedTl ? JSON.parse(selectedTl.row as any) as Invoice2 : null;

  if (!invoice) return;

  // Check if this is a Xero integration
  const linkedIntegrationDetails = state.TagLevelIntegration.linkedIntegrationDetails;
  const isXero = linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero;

  const exportTarget: InvoiceExportTarget = isXero ? "xero" : "qbo";
  const exportStartedAt = Date.now();
  trackInvoiceExportAttempted({
    target: exportTarget,
    invoiceCount: 1,
    fieldMapping: invoice.docType,
  });
  const trackSuccess = () =>
    trackInvoiceExportSucceeded({
      target: exportTarget,
      invoiceCount: 1,
      durationMs: Date.now() - exportStartedAt,
    });
  const trackFailure = (err: any) =>
    trackInvoiceExportFailed({
      target: exportTarget,
      errorCode: err?.errorCode || err?.status,
      errorMessage: err?.message || String(err),
      durationMs: Date.now() - exportStartedAt,
    });

  const linesCopy = values.lines.map(line => {
    if (isXero) {
      // Xero: only use Xero fields, no fallback to QuickBooks fields
      return {
        ...line,
        category: line.categoryXeroEdited || line.categoryXeroAutomated || line.categoryXero,
        service: line.serviceXeroEdited || line.serviceXeroAutomated || line.serviceXero,
        tax: line.taxXeroEdited || line.taxXeroAutomated || line.taxXero,
      }
    } else {
      // QuickBooks: use standard priority
      return {
        ...line,
        category: line.categoryEdited || line.categoryAutomated,
        service: line.serviceEdited || line.serviceAutomated,
        tax: line.taxEdited || line.taxAutomated || line.tax,
      }
    }
  });

  const newValues = {
    ...values,
    lines: linesCopy
  };

  const qboCustomers = state.QboCustomer.qboCustomers
  const qboVendors = state.QboVendor.qboVendors
  const qboAccounts = state.QboAccount.qboAccounts
  const xeroContacts = state.XeroContact.xeroContacts

  // For Xero, prioritize Xero-specific fields; for QuickBooks, use standard fields
  const vendor2 = isXero
    ? newValues.vendorXeroEdited || newValues.vendorXeroAutomated || newValues.vendorXero
    : newValues.vendorEdited || newValues.vendorAutomated || newValues.vendor;
  const customer2 = isXero
    ? newValues.customerXeroEdited || newValues.customerXeroAutomated || newValues.customerXero
    : newValues.customerEdited || newValues.customerAutomated || newValues.customer;
  const account2 = isXero
    ? newValues.accountXeroEdited || newValues.accountXeroAutomated || newValues.accountXero
    : newValues.accountEdited || newValues.accountAutomated || newValues.account;

  if (invoice.docType === "invoice") {
    if (isXero) {
      // Xero invoice creation - backend reads all data from transaction
      if (!isNullOrUndefined(newValues.integration) && customer2) {
        dispatch(setImportToQboLoading(true));
        createXeroInvoice2(
          newValues.integration as any,
          selectedTl?.id as any,
          { forceImport }
        )
          .then((invoicereps) => {
            const updatedValues = {
              ...newValues,
              // Xero link format - adjust based on actual response structure
              link: invoicereps.invoice?.Url || invoicereps.invoice?.url || `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoicereps.invoice?.InvoiceID}`,
              status: "published",
            };
            if (job && job?.id) {
              updateJobTransaction(job?.id, selectedTl?.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            } else {
              updateJobTransactionTagLevel(selectedTl?.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            }

            dispatch(updateTransactionLines(
              state.InvoiceManager.transactionLines.map((tl) => {
                if (tl.id === selectedTl?.id) {
                  return {
                    ...tl,
                    row: JSON.stringify(updatedValues) as any,
                    // Update metadata from API response to refresh Export & Sync section
                    metadata: invoicereps.transaction?.metadata ?? tl.metadata,
                  }
                }
                return tl;
              })
            ));

            dispatch(
              createAlert({
                message: "Invoice created successfully",
                timeout: 0,
                type: "success",
              })
            );
            dispatch(setDuplicateError(null));
            trackSuccess();
          })
          .catch((err) => {
            trackFailure(err);
            if (err?.errorCode === 1093) {
              dispatch(setDuplicateError({
                qboUrl: err.data.qboUrl || err.data.xeroUrl || err.data.url,
                docType: "invoice"
              }));
            } else {
              dispatch(
                createAlert({
                  message: err.message || "Error creating invoice. Please try again.",
                  timeout: 0,
                  type: "error",
                })
              );
            }
          }).finally(() => {
            dispatch(setImportToQboLoading(false));
          });
      }
    } else {
      // QuickBooks invoice creation
      const c: QboCustomer[] = qboCustomers.filter(
        (x) => x.value === customer2
      );
      if (!isNullOrUndefined(newValues.integration) && c.length > 0) {
        dispatch(setImportToQboLoading(true));
        createQboInvoice2(
          newValues.integration as any,
          selectedTl?.id as any,
          c[0],
          { forceImport }
        )
          .then((invoicereps) => {
            const updatedValues = {
              ...newValues,
              link: `https://app.qbo.intuit.com/app/${invoice.docType}?txnId=${invoicereps.invoice.Id}`,
              status: "published",
            };
            if (job && job?.id) {
              updateJobTransaction(job?.id, selectedTl?.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            } else {
              updateJobTransactionTagLevel(selectedTl?.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            }

            dispatch(updateTransactionLines(
              state.InvoiceManager.transactionLines.map((tl) => {
                if (tl.id === selectedTl?.id) {
                  return {
                    ...tl,
                    row: JSON.stringify(updatedValues) as any,
                    // Update metadata from API response to refresh Export & Sync section
                    metadata: invoicereps.transaction?.metadata ?? tl.metadata,
                  }
                }
                return tl;
              })
            ));

            dispatch(
              createAlert({
                message: "Invoice created successfully",
                timeout: 0,
                type: "success",
              })
            );
            dispatch(setDuplicateError(null));
            trackSuccess();
          })
          .catch((err) => {
            trackFailure(err);
            if (err?.errorCode === 1093) {
              dispatch(setDuplicateError({
                qboUrl: err.data.qboUrl,
                docType: "invoice"
              }));
            } else {
              dispatch(
                createAlert({
                  message: err.message || "Error creating invoice. Please try again.",
                  timeout: 0,
                  type: "error",
                })
              );
            }
          }).finally(() => {
            dispatch(setImportToQboLoading(false));
          });
      }
    }
  }

  if (invoice.docType === "bill") {
    if (isXero) {
      // Xero bill creation (ACCPAY) - backend reads all data from transaction
      if (
        !isNullOrUndefined(newValues.integration) &&
        vendor2 &&
        selectedTl
      ) {
        dispatch(setImportToQboLoading(true));
        createXeroBill2(
          newValues.integration as any,
          selectedTl.id as any,
          { forceImport }
        )
          .then((bill) => {
            const updatedValues = {
              ...newValues,
              // Xero link format - adjust based on actual response structure
              link: bill.bill?.Url || bill.bill?.url || `https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=${bill.bill?.InvoiceID}`,
              status: "published",
            };
            if (job && job.id) {
              updateJobTransaction(job?.id, selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            } else {
              updateJobTransactionTagLevel(selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            }

            dispatch(updateTransactionLines(
              state.InvoiceManager.transactionLines.map((tl) => {
                if (tl.id === selectedTl?.id) {
                  return {
                    ...tl,
                    row: JSON.stringify(updatedValues) as any,
                    // Update metadata from API response to refresh Export & Sync section
                    metadata: bill.transaction?.metadata ?? tl.metadata,
                  }
                }
                return tl;
              })
            ));

            dispatch(
              createAlert({
                message: "Bill created successfully",
                timeout: 0,
                type: "success",
              })
            );
            dispatch(setDuplicateError(null));
            trackSuccess();
          })
          .catch((err) => {
            trackFailure(err);
            if (err?.errorCode === 1093) {
              dispatch(setDuplicateError({
                qboUrl: err.data.qboUrl || err.data.xeroUrl || err.data.url,
                docType: "bill"
              }));
            } else {
              dispatch(
                createAlert({
                  message: err.message || "Error creating bill. Please try again.",
                  timeout: 0,
                  type: "error",
                })
              );
            }
          }).finally(() => {
            dispatch(setImportToQboLoading(false));
          });
      }
    } else {
      // QuickBooks bill creation
      const v: QboVendor[] = qboVendors.filter(
        (x) => x.value === vendor2
      );
      // const a: QboAccount[] = qboAccounts.filter(
      //   (x) => x.value === newValues.account
      // );
      if (
        !isNullOrUndefined(newValues.integration) &&
        v.length > 0 &&
        // a.length > 0 &&
        selectedTl
      ) {
        dispatch(setImportToQboLoading(true));
        createQboBill2(
          newValues.integration as any,
          selectedTl.id as any,
          v[0],
          // a[0],
          { forceImport }
        )
          .then((bill) => {
            const updatedValues = {
              ...newValues,
              link: `https://app.qbo.intuit.com/app/${invoice.docType}?txnId=${bill.bill.Id}`,
              status: "published",
            };
            if (job && job.id) {
              updateJobTransaction(job?.id, selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            } else {
              updateJobTransactionTagLevel(selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            }

            dispatch(updateTransactionLines(
              state.InvoiceManager.transactionLines.map((tl) => {
                if (tl.id === selectedTl?.id) {
                  return {
                    ...tl,
                    row: JSON.stringify(updatedValues) as any,
                    // Update metadata from API response to refresh Export & Sync section
                    metadata: bill.transaction?.metadata ?? tl.metadata,
                  }
                }
                return tl;
              })
            ));

            dispatch(
              createAlert({
                message: "Bill created successfully",
                timeout: 0,
                type: "success",
              })
            );
            dispatch(setDuplicateError(null));
            trackSuccess();
          })
          .catch((err) => {
            trackFailure(err);
            if (err?.errorCode === 1093) {
              dispatch(setDuplicateError({
                qboUrl: err.data.qboUrl,
                docType: "bill"
              }));
            } else {
              dispatch(
                createAlert({
                  message: err.message || "Error creating bill. Please try again.",
                  timeout: 0,
                  type: "error",
                })
              );
            }
          }).finally(() => {
            dispatch(setImportToQboLoading(false));
          });
      }
    }
  }

  if (invoice.docType === "expense") {
    // Note: Xero doesn't support "expense" type - only "bill" (ACCPAY)
    // This section is only for QuickBooks
    if (!isXero) {
      // const a: QboAccount[] = qboAccounts.filter(
      //   (x) => x.value === newValues.account
      // );
      // const b: QboCustomer[] = qboCustomers.filter(
      //   (x) => x.value === newValues.customer
      // );
      const v: QboVendor[] = qboVendors.filter(
        (x) => x.value === vendor2
      );
      const a: QboAccount[] = qboAccounts.filter(
        (x) => x.value === account2
      );

      if (
        !isNullOrUndefined(newValues.integration) &&
        a.length > 0 &&
        selectedTl
      ) {
        dispatch(setImportToQboLoading(true));
        createQboExpense2(
          newValues.integration as any,
          selectedTl.id as any,
          a[0],
          v[0],
          { forceImport }
        )
          .then((expense) => {
            // console.log({ a, b })
            const updatedValues = {
              ...newValues,
              status: "published",
              link: `https://app.qbo.intuit.com/app/${invoice.docType}?txnId=${expense.purchase.Id}`,
            };
            if (job && job.id) {
              updateJobTransaction(job?.id, selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            } else {
              updateJobTransactionTagLevel(selectedTl.id as any, {
                newValue: JSON.stringify(updatedValues),
              });
            }

            dispatch(updateTransactionLines(
              state.InvoiceManager.transactionLines.map((tl) => {
                if (tl.id === selectedTl?.id) {
                  return {
                    ...tl,
                    row: JSON.stringify(updatedValues) as any,
                    // Update metadata from API response to refresh Export & Sync section
                    metadata: expense.transaction?.metadata ?? tl.metadata,
                  }
                }
                return tl;
              })
            ));

            dispatch(
              createAlert({
                message: "Expense created successfully",
                timeout: 0,
                type: "success",
              })
            );
            dispatch(setDuplicateError(null));
            trackSuccess();
          })
          .catch((err) => {
            trackFailure(err);
            if (err?.errorCode === 1093) {
              dispatch(setDuplicateError({
                qboUrl: err.data.qboUrl,
                docType: "expense"
              }));
            } else {
              dispatch(
                createAlert({
                  message: err.message || "Error creating expense. Please try again.",
                  timeout: 0,
                  type: "error",
                })
              );
            }
          }).finally(() => {
            dispatch(setImportToQboLoading(false));
          });
      }
    }
  }
};

export const applyRunInvoiceRules = (integrationId: number | null = null, docType: DocType, transactionLineId?: number) => async (dispatch, getState) => {
  try {
    const state: ReduxState = getState();
    const job = state.JobData.job;
    const { selectedL1, selectedL2, } = state.TagLevel;

    // if (!job) return;

    dispatch(setRunInvoiceRulesLoading(true));

    let fn;
    if (job && job.id) {
      fn = runInvoiceRules(job.id, integrationId, docType, transactionLineId)
    }
    else {
      fn = runInvoiceRulesTagLevel(selectedL1, selectedL2, integrationId, docType, transactionLineId)
    }


    // const { newTls } = await runInvoiceRules(job.id, integrationId, docType)
    const { newTls } = await fn;
    dispatch(updateTransactionLines(newTls));

    const mapTlsById = newTls.reduce((acc, tl) => {
      acc[tl.id] = tl;
      return acc;
    }, {});

    dispatch(updateTransactionLines(
      state.InvoiceManager.transactionLines.map((tl, index) => {
        const newTl = tl?.id ? mapTlsById[tl.id] : null;
        if (newTl) {
          return { ...tl, ...newTl };
        }
        return tl;
      })
    ));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRunInvoiceRulesLoading(false));
  }
};

// const STORAGE_KEY = "invoice-export-selected-fields";

// export const loadSavedExportFields = (availableFields: {
//   regular: string[];
//   extra: string[];
//   lineItem: string[];
//   lineItemExtra: string[];
// }) => (dispatch) => {
//   try {
//     const savedFields = localStorage.getItem(STORAGE_KEY);
//     if (savedFields) {
//       const parsedFields = JSON.parse(savedFields);

//       const standardFields = parsedFields.filter((field: string) =>
//         !field.startsWith("extra.") && !field.startsWith("lineItem.")
//       );
//       const extraFields = parsedFields.filter((field: string) =>
//         field.startsWith("extra.") && !field.startsWith("lineItem.")
//       ).map((field: string) => field.replace("extra.", ""));
//       const lineItemFields = parsedFields.filter((field: string) =>
//         field.startsWith("lineItem.") && !field.startsWith("lineItem.extra.")
//       ).map((field: string) => field.replace("lineItem.", ""));
//       const lineItemExtraFields = parsedFields.filter((field: string) =>
//         field.startsWith("lineItem.extra.")
//       ).map((field: string) => field.replace("lineItem.extra.", ""));

//       // Filter fields to ensure they exist in available fields
//       const validStandardFields = standardFields.filter((field: string) =>
//         availableFields.regular.includes(field)
//       );
//       const validExtraFields = extraFields.filter((field: string) =>
//         availableFields.extra.includes(field)
//       );
//       const validLineItemFields = lineItemFields.filter((field: string) =>
//         availableFields.lineItem.includes(field)
//       );
//       const validLineItemExtraFields = lineItemExtraFields.filter((field: string) =>
//         availableFields.lineItemExtra.includes(field)
//       );

//       dispatch(setInvoiceFieldStandard(validStandardFields));
//       dispatch(setInvoiceFieldExtra(validExtraFields));
//       dispatch(setLineItemFieldStandard(validLineItemFields));
//       dispatch(setLineItemFieldExtra(validLineItemExtraFields));
//     }
//   } catch (e) {
//     console.warn("Failed to parse saved fields from localStorage");
//   }
// };

export default invoiceManagerSlice.reducer;